import React from 'react';

import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import { Backdrop, CircularProgress, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';

import PaginationControl from '../../../common/components/PaginationControl';

import '../TableStyles.scss';
import { Link } from 'react-router-dom';

import { Pages } from '../../../common/constants/Pages';
import { Organization } from '../../../common/types/Organization';
import { ReactComponent as DeleteIcon } from '../../../common/assets/icons/delete_icon.svg';

import DeleteOrganizationPopup from 'src/admin/screens/OrganizationsScreen/components/DeleteOrganizationPopup';

export type Order = 'asc' | 'desc';

type HeaderCell = {
  id: keyof Organization;
  label: string;
  numeric: boolean;
};

const headerCells: readonly HeaderCell[] = [
  {
    id: 'id',
    label: 'ID',
    numeric: false,
  },
  {
    id: 'name',
    label: 'Name',
    numeric: false,
  },
  {
    id: 'hasActiveSubscription',
    label: 'Subscription',
    numeric: false,
  },

  // {
  //   id: "mobileSubscriptions",
  //   label: "Mobile Subscriptions",
  //   numeric: true,
  // },
  {
    id: 'desktopSubscriptions',
    label: 'Seats',
    numeric: true,
  },
  {
    id: 'totalScanSearches',
    label: 'Note Scans',
    numeric: true,
  },
  {
    id: 'totalKeywordSearches',
    label: 'Keyword Searches',
    numeric: true,
  },
  {
    id: 'NPI',
    label: '',
    numeric: false,
  },
];

interface OrganizationsTableHeadProps {
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof Organization
  ) => void;
  order: Order;
  orderBy: string;
}

const OrganizationTableHead: React.FC<OrganizationsTableHeadProps> = ({
  onRequestSort,
  order = 'desc',
  orderBy = 'name',
}) => {
  const createSortHandler =
    (property: keyof Organization) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  return (
    <TableHead
      sx={{
        backgroundColor: (theme) => theme.palette.primaryLight?.main,
        height: 52,
      }}
    >
      <TableRow>
        {headerCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            sortDirection={orderBy === headCell.id ? order : false}
            align={
              headCell.id === 'name' || headCell.id === 'id'
                ? 'left'
                : headCell.id === 'NPI'
                ? 'right'
                : 'center'
            }
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
              hideSortIcon={
                headCell.id === 'id' ||
                headCell.id === 'NPI' ||
                headCell.id === 'hasActiveSubscription'
              }
              IconComponent={() => {
                if (orderBy !== headCell.id) {
                  return <KeyboardArrowUpRoundedIcon />;
                }
                return order === 'desc' ? (
                  <KeyboardArrowDownRoundedIcon />
                ) : (
                  <KeyboardArrowUpRoundedIcon />
                );
              }}
            >
              {headCell.label}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};

type OrganizationTableProps = {
  organizations: Organization[];
  isLoading: boolean;
  total: number;
  page: number;
  setPage: (page: number) => void;
  rowsPerPage: number;
  requestSort: (property: keyof Organization, order: Order) => void;
};

const OrganizationsTable: React.FC<OrganizationTableProps> = ({
  isLoading,
  page,
  requestSort,
  organizations,
  rowsPerPage,
  setPage,
  total,
}) => {
  const [order, setOrder] = React.useState<Order>('desc');
  const [orderBy, setOrderBy] = React.useState<keyof Organization>('name');
  const [orgTodelete, setOrgToDelete] = React.useState<Organization | null>(
    null
  );

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof Organization
  ) => {
    if (property === 'NPI') return;
    if (property === 'id') return;
    if (property === 'hasActiveSubscription') return;

    const isAsc = orderBy === property && order === 'asc';
    requestSort(property, isAsc ? 'desc' : 'asc');
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  return (
    <Box sx={{ minWidth: 400 }}>
      <Backdrop
        open={!!orgTodelete}
        sx={{
          color: '#fff',
          zIndex: 99,
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        {orgTodelete && (
          <DeleteOrganizationPopup
            onClose={() => setOrgToDelete(null)}
            organization={orgTodelete}
          />
        )}
      </Backdrop>

      <TableContainer>
        <Table>
          <OrganizationTableHead
            onRequestSort={handleRequestSort}
            order={order}
            orderBy={orderBy}
          />
          {isLoading && (
            <TableBody>
              <TableRow>
                <TableCell sx={{ border: 0 }} colSpan={6}>
                  <CircularProgress
                    color='primary'
                    size='64px'
                    style={{ display: 'block', margin: '24px auto' }}
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          )}
          {!isLoading && (
            <TableBody>
              {organizations.map((org, index) => (
                <TableRow
                  itemID={`tr-${org.name.toString()}`}
                  key={index.toString()}
                  className={'admin-table-row'}
                  sx={{
                    '&:last-child td, &:last-child th': { border: 0 },
                  }}
                >
                  <TableCell>
                    <Link
                      className='unstyled-link full-width-block'
                      to={Pages.getRoute(Pages.editOrganization, org.id)}
                    >
                      {org.id}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Link
                      className='unstyled-link full-width-block'
                      to={Pages.getRoute(Pages.editOrganization, org.id)}
                    >
                      {org.name}
                    </Link>
                  </TableCell>
                  <TableCell align={'center'}>
                    <Link
                      className='unstyled-link full-width-block'
                      to={Pages.getRoute(Pages.editOrganization, org.id)}
                    >
                      <Typography color='secondary'>
                        {org.hasActiveSubscription ? 'Active' : 'Inactive'}
                      </Typography>
                    </Link>
                  </TableCell>
                  {/* <TableCell align={"center"}>
                    <Link
                      className="unstyled-link full-width-block"
                      to={Pages.getRoute(Pages.editOrganization, org.id)}>
                      <Typography color="secondary">
                        {org.mobileSubscriptions}
                      </Typography>
                    </Link>
                  </TableCell> */}
                  <TableCell align={'center'}>
                    <Link
                      className='unstyled-link full-width-block'
                      to={Pages.getRoute(Pages.editOrganization, org.id)}
                    >
                      <Typography color='secondary'>
                        {org.desktopSubscriptions}
                      </Typography>
                    </Link>
                  </TableCell>
                  <TableCell align={'center'}>
                    <Link
                      className='unstyled-link full-width-block'
                      to={Pages.getRoute(Pages.editOrganization, org.id)}
                    >
                      <Typography color='secondary'>
                        {org.totalScanSearches}
                      </Typography>
                    </Link>
                  </TableCell>
                  <TableCell align={'center'}>
                    <Link
                      className='unstyled-link full-width-block'
                      to={Pages.getRoute(Pages.editOrganization, org.id)}
                    >
                      <Typography color='secondary'>
                        {org.totalKeywordSearches}
                      </Typography>
                    </Link>
                  </TableCell>
                  <TableCell align={'right'}>
                    <IconButton onClick={() => setOrgToDelete(org)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          )}
        </Table>
      </TableContainer>
      {!isLoading && organizations.length > 0 && (
        <PaginationControl
          count={Math.ceil(total / rowsPerPage)}
          onChange={(e, p) => setPage(p)}
          page={page}
        />
      )}
    </Box>
  );
};

export default OrganizationsTable;
